import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

export const COMMAND_PALETTE_RECENT_KEY = "beetegre.commandPalette.recent:v1";
const MAX_RECENT = 5;

export interface PaletteNavGroup {
  labelKey: string;
  items: PaletteNavItem[];
}

export interface PaletteNavItem {
  labelKey: string;
  path: string;
}

export interface RecentEntry {
  path: string;
  labelKey: string;
  visitedAt: number;
}

function loadRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(COMMAND_PALETTE_RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function pushRecent(entry: Omit<RecentEntry, "visitedAt">): void {
  const existing = loadRecent().filter((r) => r.path !== entry.path);
  const next: RecentEntry[] = [
    { ...entry, visitedAt: Date.now() },
    ...existing,
  ].slice(0, MAX_RECENT);
  try {
    localStorage.setItem(COMMAND_PALETTE_RECENT_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable; silent — not business-critical
  }
}

interface FlatItem {
  labelKey: string;
  path: string;
  groupLabelKey: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  navGroups: PaletteNavGroup[];
  navigate: (path: string) => void;
}

const TopbarCommandPalette: React.FC<Props> = ({
  open,
  onClose,
  navGroups,
  navigate,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [recent, setRecent] = useState<RecentEntry[]>([]);

  const flatItems: FlatItem[] = useMemo(
    () =>
      navGroups.flatMap((g) =>
        g.items.map((item) => ({
          labelKey: item.labelKey,
          path: item.path,
          groupLabelKey: g.labelKey,
        })),
      ),
    [navGroups],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flatItems;
    return flatItems.filter(
      (item) =>
        t(item.labelKey).toLowerCase().includes(q) ||
        item.path.toLowerCase().includes(q),
    );
  }, [query, flatItems, t]);

  const filteredRecent = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recent;
    return recent.filter(
      (r) =>
        t(r.labelKey).toLowerCase().includes(q) ||
        r.path.toLowerCase().includes(q),
    );
  }, [query, recent, t]);

  // INVARIANT: This component is mounted iff `open === true`. The `if (!open) return null`
  // guard below means a remount happens on every open transition (false → true), so a
  // `useLayoutEffect([])` here fires exactly once per open — equivalent to a reactive
  // `useEffect([open])` that early-returns when closed. If the early-return is removed,
  // this comment is wrong: re-introduce `useEffect([open])` instead of leaving this here.
  // reason: one-shot on mount — this component is only mounted when open===true
  // (see INVARIANT comment above). setRecent here is semantically equivalent to
  // useState(() => loadRecent()) but deferred to layout time to avoid SSR
  // hydration mismatch. localStorage reads in useState initializers are safe but
  // the requestAnimationFrame focus call must be in an effect; keeping both
  // together in one useLayoutEffect is cleaner than splitting them.
  /* eslint-disable react-hooks/set-state-in-effect */
  useLayoutEffect(() => {
    setRecent(loadRecent());
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  const totalCount = filteredRecent.length + filtered.length;

  const handleSelect = useCallback(
    (item: { path: string; labelKey: string }) => {
      pushRecent({ path: item.path, labelKey: item.labelKey });
      navigate(item.path);
      onClose();
    },
    [navigate, onClose],
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, totalCount - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const allItems = [
          ...filteredRecent.map((r) => ({
            path: r.path,
            labelKey: r.labelKey,
          })),
          ...filtered.map((f) => ({ path: f.path, labelKey: f.labelKey })),
        ];
        const target = allItems[selectedIndex];
        if (target) handleSelect(target);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, totalCount, filteredRecent, filtered, selectedIndex, handleSelect]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      '[aria-selected="true"]',
    );
    el?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex]);

  if (!open) return null;

  let globalIndex = 0;

  return createPortal(
    <div className="fixed inset-0 z-60 flex justify-center px-4 pt-[12vh] pb-6">
      {/* Backdrop — dışa tıklama burada kapatır; ayrı bir mousedown dinleyicisine
          gerek yok. */}
      <div
        className="absolute inset-0 bg-background/70 backdrop-blur-[2px] motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150"
        aria-hidden="true"
        onMouseDown={onClose}
      />

      <div
        id="cmd-palette-dropdown"
        role="dialog"
        aria-modal="true"
        aria-label={t("topbar.commandPalette.aria")}
        className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-popover shadow-2xl motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-150"
        style={{ maxHeight: "min(70vh, 32rem)" }}
      >
      {/* Search input inside palette */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border shrink-0 focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring">
        <Search
          size={17}
          className="text-muted-foreground shrink-0"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="cmd-palette-list"
          aria-label={t("topbar.commandPalette.aria")}
          placeholder={t("topbar.commandPalette.searchPlaceholder")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedIndex(0);
          }}
          className="flex-1 bg-transparent text-[0.9375rem] text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        <kbd className="max-md:hidden inline-flex items-center gap-0.5 rounded border border-border bg-muted px-1.5 py-0.5 text-[0.625rem] font-medium text-muted-foreground select-none pointer-events-none">
          Esc
        </kbd>
      </div>

      {/* Results list */}
      <ul
        id="cmd-palette-list"
        ref={listRef}
        role="listbox"
        aria-label={t("topbar.commandPalette.aria")}
        className="flex-1 overflow-y-auto min-h-0 py-1"
      >
        {/* aria-live region announcing result count; visible empty state below carries the empty text */}
        <li role="presentation" aria-live="polite" className="sr-only">
          {totalCount > 0 ? `${totalCount}` : ""}
        </li>

        {/* Recent section */}
        {filteredRecent.length > 0 && (
          <>
            <li
              role="presentation"
              className="px-3 pt-1.5 pb-0.5 text-[0.65rem] font-semibold uppercase tracking-widest text-muted-foreground"
            >
              {t("topbar.commandPalette.sectionRecent")}
            </li>
            {filteredRecent.map((r) => {
              const idx = globalIndex++;
              const isSelected = idx === selectedIndex;
              return (
                <li key={`recent-${r.path}`} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={cn(
                      "w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm transition-colors",
                      isSelected
                        ? "bg-accent text-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground",
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect({ path: r.path, labelKey: r.labelKey });
                    }}
                  >
                    <Search
                      size={13}
                      aria-hidden="true"
                      className="shrink-0 opacity-50"
                    />
                    {t(r.labelKey)}
                  </button>
                </li>
              );
            })}
          </>
        )}

        {/* Grouped pages */}
        {filtered.length > 0 &&
          (() => {
            const groups = navGroups.flatMap((g) => {
              const items = filtered.filter(
                (f) => f.groupLabelKey === g.labelKey,
              );
              return items.length > 0 ? [{ labelKey: g.labelKey, items }] : [];
            });

            return groups.map((g) => (
              <React.Fragment key={g.labelKey}>
                <li
                  role="presentation"
                  className="px-3 pt-2 pb-0.5 text-[0.65rem] font-semibold uppercase tracking-widest text-muted-foreground"
                >
                  {t(g.labelKey)}
                </li>
                {g.items.map((item) => {
                  const idx = globalIndex++;
                  const isSelected = idx === selectedIndex;
                  return (
                    <li key={item.path} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={cn(
                          "w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm transition-colors",
                          isSelected
                            ? "bg-accent text-foreground"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground",
                        )}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelect({
                            path: item.path,
                            labelKey: item.labelKey,
                          });
                        }}
                      >
                        {t(item.labelKey)}
                      </button>
                    </li>
                  );
                })}
              </React.Fragment>
            ));
          })()}

        {/* Empty state */}
        {totalCount === 0 && (
          <li
            role="presentation"
            className="py-8 text-center text-sm text-muted-foreground"
          >
            {t("topbar.commandPalette.empty")}
          </li>
        )}
      </ul>

      {/* Footer hint */}
      <div className="shrink-0 border-t border-border px-4 py-2 flex items-center gap-3 text-[0.6875rem] text-muted-foreground">
        <span>
          <kbd className="inline-flex items-center rounded border border-border bg-muted px-1 py-0.5 text-[0.625rem] font-medium mr-1">
            {"↑↓"}
          </kbd>
          {t("topbar.commandPalette.openHint")}
        </span>
      </div>
      </div>
    </div>,
    document.body,
  );
};

export default TopbarCommandPalette;
